import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, MapPin, Building2 } from "lucide-react";

const COMPANY = "Khat Alebtkar Facilities Management Services LLC";
const EMAIL = "contact@aureontrader.com";

function LegalLayout({ kicker, title, updated, children, testid }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div data-testid={testid} className="min-h-screen bg-ink text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/50 transition-colors hover:text-gold">
            <ArrowLeft className="h-3.5 w-3.5" />
            Aureon
          </Link>
          <Link to="/contact" className="text-[11px] uppercase tracking-[0.2em] text-gold transition-colors hover:text-gold-light">
            Contact ↗
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-6 pb-24 pt-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-gold">{kicker}</p>
          <h1 className="font-serif text-4xl font-light tracking-tighter text-white sm:text-5xl">{title}</h1>
          {updated && <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-white/30">Last updated · {updated}</p>}
          <div className="mt-10 space-y-10">{children}</div>
        </motion.div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="mb-3 text-base uppercase tracking-[0.2em] text-gold md:text-lg">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-white/60">{children}</div>
    </section>
  );
}

const UPDATED = "August 2026";

export function TermsPage() {
  useEffect(() => { document.title = "Terms of Service — Aureon Global Markets"; }, []);
  return (
    <LegalLayout kicker="Legal" title="Terms of Service" updated={UPDATED} testid="terms-page">
      <Section title="1. The service">
        <p>
          Aureon Global Markets ("Aureon", "we", "the platform"), operated by {COMPANY}, provides a
          private, simulated trading terminal. All trading on the platform takes place in a
          simulated environment using live market data for educational and training purposes.
          No real assets are bought, sold, held, or custodied on your behalf.
        </p>
      </Section>
      <Section title="2. Trading credits">
        <p>
          Access tiers are purchased once and credited to your terminal account one-for-one as
          trading credits (for example, a $100 purchase credits a $100 balance). Trading credits
          are a usage right inside the simulation only: they have no cash value, are not a deposit,
          an investment, or a financial instrument, and cannot be withdrawn, exchanged, or redeemed
          for money or cryptocurrency.
        </p>
        <p>
          The terminal includes a <strong className="text-white/80">simulated withdrawal flow</strong> so
          users can learn how payouts work on a real trading platform: entering a wallet address,
          requesting a payout, cooldown periods, and confirmation emails. This flow is educational
          only — no real funds are sent, requested, or owed in either direction.
        </p>
        <p>
          Daily boost codes and referral bonuses are discretionary promotional credits granted under
          the same terms. They exist solely inside the simulation.
        </p>
      </Section>
      <Section title="3. Payments and refunds">
        <p>
          Payments are processed securely by Stripe. A purchase is final once terminal access is
          granted. If a payment is completed but no access is delivered, contact us at{" "}
          <a href={`mailto:${EMAIL}`} className="text-gold hover:text-gold-light">{EMAIL}</a> and we
          will make it right.
        </p>
      </Section>
      <Section title="4. Market data">
        <p>
          Prices, charts, and order books are assembled from third-party public market data
          providers and may be delayed, aggregated, or temporarily unavailable. Data is provided
          for simulation purposes only and must not be relied upon for real trading decisions.
        </p>
      </Section>
      <Section title="5. Acceptable use">
        <p>
          You agree not to abuse the platform, attempt to exploit the simulation, share or resell
          access codes, or use the service for any unlawful purpose. We may suspend access for
          abuse. One access code per account holder.
        </p>
      </Section>
      <Section title="6. No financial advice">
        <p>
          Nothing on Aureon constitutes investment, legal, or tax advice. The platform is a training
          environment. See our <Link to="/risk" className="text-gold hover:text-gold-light">Risk Disclaimer</Link>.
        </p>
      </Section>
      <Section title="7. Liability">
        <p>
          The service is provided "as is" without warranties of any kind. To the maximum extent
          permitted by law, our aggregate liability is limited to the amount you paid for your
          current access tier.
        </p>
      </Section>
      <Section title="8. Contact">
        <p>
          Questions about these terms: <a href={`mailto:${EMAIL}`} className="text-gold hover:text-gold-light">{EMAIL}</a> · {COMPANY}, Dubai, United Arab Emirates.
        </p>
      </Section>
    </LegalLayout>
  );
}

export function PrivacyPage() {
  useEffect(() => { document.title = "Privacy Policy — Aureon Global Markets"; }, []);
  return (
    <LegalLayout kicker="Legal" title="Privacy Policy" updated={UPDATED} testid="privacy-page">
      <Section title="1. What we collect">
        <p>
          We collect the minimum needed to run the service: your email address (collected by Stripe
          at checkout), your access code activity, simulated trading activity, and referral usage.
          We never see or store your full card details — payments are handled entirely by Stripe.
        </p>
      </Section>
      <Section title="2. How we use it">
        <p>
          Your email is used to deliver your access code, daily boost codes you are subscribed to,
          and service updates. Trading data is used to operate and improve the simulation.
          We do not sell your data to anyone.
        </p>
      </Section>
      <Section title="3. Third-party processors">
        <p>
          Stripe (payments), Resend (transactional email), and public market-data providers
          (price feeds). Each processes data under its own privacy policy.
        </p>
      </Section>
      <Section title="4. Your rights">
        <p>
          You may request a copy, correction, or deletion of your personal data at any time by
          emailing <a href={`mailto:${EMAIL}`} className="text-gold hover:text-gold-light">{EMAIL}</a>.
          Deleting your data removes terminal access.
        </p>
      </Section>
      <Section title="5. Retention and security">
        <p>
          We retain transaction records as required for accounting. Data is stored with reputable
          cloud providers and access is restricted. No method of transmission is 100% secure, but we
          work to protect your information.
        </p>
      </Section>
    </LegalLayout>
  );
}

export function RiskPage() {
  useEffect(() => { document.title = "Risk Disclaimer — Aureon Global Markets"; }, []);
  return (
    <LegalLayout kicker="Legal" title="Risk Disclaimer" updated={UPDATED} testid="risk-page">
      <Section title="Simulation only">
        <p>
          Aureon is a simulated trading environment. Balances shown in the terminal are trading
          credits inside the simulation. Nothing traded on Aureon is a real transaction, and no real
          profit or loss occurs.
        </p>
      </Section>
      <Section title="Real trading carries real risk">
        <p>
          Trading cryptocurrencies, equities, metals, or foreign exchange with real money involves
          substantial risk of loss and is not suitable for everyone. Past performance — simulated or
          real — does not guarantee future results. Never trade with money you cannot afford to lose,
          and consider seeking advice from a licensed financial adviser.
        </p>
      </Section>
      <Section title="Market data">
        <p>
          Quotes and charts come from public third-party feeds and may be delayed or inaccurate.
          Simulated fills at displayed prices do not represent achievable real-market execution.
        </p>
      </Section>
      <Section title="No advice">
        <p>
          Aureon and {COMPANY} do not provide investment advice, portfolio management, or brokerage
          services. You are solely responsible for any real-world trading decisions you make.
        </p>
      </Section>
    </LegalLayout>
  );
}

export function ContactPage() {
  useEffect(() => { document.title = "Contact — Aureon Global Markets"; }, []);
  return (
    <LegalLayout kicker="Company" title="Contact & Company" testid="contact-page">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="border border-white/10 bg-surface p-6" data-testid="contact-company">
          <Building2 className="mb-4 h-5 w-5 text-gold" />
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Operated by</p>
          <p className="mt-2 text-sm leading-relaxed text-white/80">{COMPANY}</p>
        </div>
        <div className="border border-white/10 bg-surface p-6" data-testid="contact-email">
          <Mail className="mb-4 h-5 w-5 text-gold" />
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Email</p>
          <a href={`mailto:${EMAIL}`} className="mt-2 block text-sm text-gold transition-colors hover:text-gold-light">
            {EMAIL}
          </a>
        </div>
        <div className="border border-white/10 bg-surface p-6" data-testid="contact-location">
          <MapPin className="mb-4 h-5 w-5 text-gold" />
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Registered in</p>
          <p className="mt-2 text-sm leading-relaxed text-white/80">Dubai, United Arab Emirates</p>
        </div>
      </div>
      <Section title="Support">
        <p>
          For access codes, payments, referrals, or anything else, email us — we reply within one to
          two business days. For payment questions, include the email address you used at checkout.
        </p>
      </Section>
      <Section title="Legal">
        <p className="flex flex-wrap gap-6">
          <Link to="/terms" className="text-gold hover:text-gold-light">Terms of Service</Link>
          <Link to="/privacy" className="text-gold hover:text-gold-light">Privacy Policy</Link>
          <Link to="/risk" className="text-gold hover:text-gold-light">Risk Disclaimer</Link>
        </p>
      </Section>
    </LegalLayout>
  );
}
